import { DatePipe } from '@angular/common';
import { Component, inject, input, OnInit } from '@angular/core';
import {
  InventoryEntityField,
  InventoryEntityFieldStatus,
  ResourceCategory,
} from 'src/app/models/api/inventoryResource.model';
import { LabelsProviderPipe } from 'src/app/pipes/labels-provider/labels-provider.pipe';
import { StatusLabelEnum } from '../../status-label/status-label.component';
import { ICardField } from 'src/app/models/viewModels/cardModel';
import { SidePanelService } from 'src/app/features/side-panel/services/side-panel.service';
import { IInventoryResourceViewModel } from 'src/app/models/viewModels/inventoryResourceViewModel';

export interface RelatedResourceInfo {
  resourceName: string;
  resourceId: string;
  resourceUrlPath: string;
  resourceStatus: StatusLabelEnum;
  creationTime: InventoryEntityField<Date>;
  createdByIdentityName: string;
  createdByIdentityId?: string;
  extraFields: ICardField[];
  resourceType: string;
}

export interface RelatedResourceCardComponentInput {
  relatedResourceInfo: RelatedResourceInfo | undefined;
  resourceCategory: string;
  relationType: string;
  isVisible: boolean;
  additionalInfoFields: ICardField[];
  integrationId: string;
}

@Component({
  selector: 'app-related-resource-card',
  templateUrl: './related-resource-card.component.html',
  styleUrls: ['./related-resource-card.component.scss'],
})
export class RelatedResourceCardComponent implements OnInit {
  public parentInput = input.required<RelatedResourceCardComponentInput>();
  public isToggleContentAllowed = input.required<boolean>();

  public title: string;
  public fields: ICardField[] = [];
  public additionalInfoFields: ICardField[];
  public relationBodyExpansionState: boolean;

  private readonly _sidePanelService = inject(SidePanelService);

  constructor(
    private readonly _labelsProviderPipe: LabelsProviderPipe,
    private readonly _datePipe: DatePipe
  ) {
    this.relationBodyExpansionState = true;
  }

  public onExploreResourceClick(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    const input = this.parentInput();
    if (input.relatedResourceInfo) {
      // Auto-stacking: will stack if panel is open, otherwise opens as main
      const resourceViewModel = {
        resource: {
          id: input.relatedResourceInfo.resourceId,
          integration: { id: input.integrationId },
        },
      } as IInventoryResourceViewModel;
      this._sidePanelService.openResourceDetails(resourceViewModel);
    }
  }

  ngOnInit(): void {
    this._init();
  }

  public updateRelationBodyAccordionExpansionState(isExpanded: boolean) {
    this.relationBodyExpansionState = isExpanded;
  }

  private _init(): void {
    const input = this.parentInput();

    this.title = input.relatedResourceInfo
      ? input.relatedResourceInfo.resourceName
      : this._labelsProviderPipe.transform('inventoryResourcePage.brokenRelationCardTitle');
    const categoryField = this._createCardField(
      'inventoryResourcePage.relatedResourceCardResourceCategoryFieldKey',
      input.resourceCategory
    );

    const relationTypeField = this._createCardField(
      'inventoryResourcePage.relatedResourceCardResourceRelationTypeFieldKey',
      input.relationType
    );

    this.fields.push(categoryField);

    if (input.relatedResourceInfo) {
      this.fields.push(
        this._createCardField(
          'inventoryResourcePage.relatedResourceCardResourceTypeFieldKey',
          input.relatedResourceInfo.resourceType
        )
      );
      this.fields.push(relationTypeField);

      if (this._isCreatedByFieldApplicable()) {
        const createdByCardField = this._getCreatedByCardField();
        createdByCardField?.let((card) => this.fields.push(card));
      }
      this.fields.push(
        this._createCardField(
          'inventoryResourcePage.relatedResourceCardCreatedAtTitle',
          this._getCreationTimeStringIfHasValue(input.relatedResourceInfo.creationTime)
        )
      );

      this.fields.push(...input.relatedResourceInfo.extraFields);
    } else {
      this.fields.push(relationTypeField);
    }

    this.additionalInfoFields = [...input.additionalInfoFields];
  }

  private _getCreationTimeStringIfHasValue(creationTime: InventoryEntityField<Date>): string {
    if (creationTime.status === InventoryEntityFieldStatus.hasValue) {
      // Empty string default for compiler only, we know at runtime there will also be a date value here
      return this._datePipe.transform(creationTime.value, 'medium') ?? '';
    }
    return this._labelsProviderPipe.transform('common.notAvailable');
  }

  private _createCardField(generalNamedKeyLabel: string, value: string): ICardField {
    return {
      key: this._labelsProviderPipe.transform(generalNamedKeyLabel),
      value: value,
    };
  }

  private _isCreatedByFieldApplicable(): boolean {
    const input = this.parentInput();

    const resourceCategory = input.resourceCategory.toLowerCase();
    if (!input.relatedResourceInfo?.createdByIdentityId) return false;
    // We know that the created by relation is not applicable for connectors and identities, so we won't show it
    return resourceCategory !== ResourceCategory.connector && resourceCategory !== ResourceCategory.identity;
  }

  private _getCreatedByCardField(): ICardField | undefined {
    const input = this.parentInput();

    if (input.relatedResourceInfo) {
      const createdByCardField: ICardField = this._createCardField(
        'inventoryResourcePage.relatedResourceCardResourceCreatedByIdentityNameTitle',
        input.relatedResourceInfo.createdByIdentityName
      );
      if (input.relatedResourceInfo.createdByIdentityId) {
        createdByCardField.internalNavigation = {
          page: '',
          query: {
            integration: input.integrationId,
            resource: input.relatedResourceInfo.createdByIdentityId,
          },
        };
      }

      return createdByCardField;
    }
    return undefined;
  }
}
